import chalk from "chalk";
import type { GLTF } from "three/examples/jsm/Addons.js";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import { getMeshes } from "@/features/model-renderer/utils/gltf.ts";
import type { Part, PartNo } from "@/features/sub-families/types";
import Logger from "@/lib/logger.ts";
import type { Nullable } from "@/types";
import { prettyBooleanText } from "@/utils/debug.ts";

export default function debugModel(model: Nullable<GLTF>, expectedParts: Part[]) {
    getMeshes(model)
        .then((meshes) => {
            Logger.debug("Verifying availability of parts in the model");

            const sortedExpectedParts = expectedParts.map(({ partNo }) => partNo).sort((a, b) => a.localeCompare(b));

            const partNumbers = meshes.filter((mesh) => mesh.userData.part).map((mesh) => mesh.userData.part as PartNo);
            const uniques = new Set(partNumbers);

            for (const expectedPart of sortedExpectedParts) {
                const expectedPartIsFound = uniques.has(expectedPart);
                const followsPartsLabel = getFollowsPartsLabel(expectedPart, expectedParts);
                const label = expectedPartIsFound ? "OK ✔" : "NOK ❌";

                const part = expectedParts.find(({ partNo }) => partNo === expectedPart);
                if (!part) return; // Should never happen

                Logger.info(
                    `Part: ${expectedPart}`,
                    prettyBooleanText(label, expectedPartIsFound),
                    followsPartsLabel ? chalk.cyan(followsPartsLabel) : undefined,
                );

                DiagnosticsStore.collectPartStatus({
                    partName: part.label,
                    partNo: expectedPart,
                    available: expectedPartIsFound,
                    image: part.assetView,
                });
            }
        })
        .catch((err) => Logger.error("Could not detect meshes", err));
}

function getFollowsPartsLabel(partNo: PartNo, parts: Part[]) {
    const followsPartNo = parts.find((part) => part.partNo === partNo)?.followsPartNo;
    const followsPart = parts.find((part) => part.partNo === followsPartNo) ?? null;

    return followsPart ? `(Follows part: ${followsPart.partNo} - ${followsPart.label})` : null;
}
